import { useTranslation } from "react-i18next";
import CollapsibleSection from "components/Section";
import { Grid } from "components/Grid";
import { LineChart } from "components/Charts/LineChart";
import { PieChart } from "components/Charts/PieChart";
import { TDataTableColumn } from "components/Grid/types";
import { getApartmentsReport } from "../api";
import { useReportData } from "../useReportData";
import { useReportFilters } from "../useReportFilters";
import { useGridDetailsContextMenu } from "components/Grid/hooks/useGridDetailsContextMenu";
import { KpiCard } from "../components/KpiCard";
import { ReportSubHeader } from "../components/ReportSubHeader";
import { ReportFiltersBar } from "../components/ReportFiltersBar";

/** Every grid on this page is capped, so the page scrolls instead of the browser. */
const GRID_HEIGHT = 380;

const topApartmentsColumns: TDataTableColumn[] = [
  { dataField: "name", caption: "apartmentName", dataType: "string" },
  { dataField: "cityName", caption: "city", dataType: "string" },
  { dataField: "unitCount", caption: "unitCount", dataType: "number" },
  { dataField: "meterCount", caption: "meterCount", dataType: "number" },
  {
    dataField: "consumption",
    caption: "consumptionCubicMeter",
    dataType: "number",
    sortOrder: "desc",
  },
];

const ApartmentsReportView = () => {
  const { t } = useTranslation();
  const { filters, applyFilters, isReady } = useReportFilters();
  const {
    data,
    isLoading,
    isRefetching,
    lastUpdated,
    lastAttempt,
    refreshIntervalMs,
    refresh,
  } = useReportData(
    getApartmentsReport,
    filters,
    isReady
  );
  const apartmentDetailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.id ? `/dashboard/apartments/details/${row.id}` : undefined
  );

  return (
    <>
      <ReportSubHeader
        title={t("apartmentsReport")}
        lastUpdated={lastUpdated}
        lastAttempt={lastAttempt}
        refreshIntervalMs={refreshIntervalMs}
        isLoading={isLoading}
        isRefetching={isRefetching}
        onRefresh={refresh}
      />
      <ReportFiltersBar
        filters={filters}
        onApply={applyFilters}
        isLoading={isLoading}
      />
      <div className="flex flex-col gap-4 p-4 h-full overflow-auto [&>*]:shrink-0">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title={t("totalApartments")}
            value={data?.totalApartments}
            iconName="MdApartment"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("totalUnits")}
            value={data?.totalUnits}
            iconName="MdMeetingRoom"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("apartmentsWithActiveMeters")}
            value={data?.apartmentsWithActiveMeters}
            iconName="MdGasMeter"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("averageUnitsPerApartment")}
            value={data?.averageUnitsPerApartment}
            iconName="MdOutlineAnalytics"
            isLoading={isLoading}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <CollapsibleSection
            title={t("monthlyRegisteredApartments")}
            className="xl:col-span-2"
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <LineChart
                series={[
                  {
                    type: "column",
                    name: t("apartments"),
                    data: data.monthlyRegistered.map((item) => item.value),
                  },
                ]}
                xAxis={{
                  categories: data.monthlyRegistered.map((item) => item.name),
                }}
                yAxis={{ title: { text: t("count") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("apartmentsByCity")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="pie"
          >
            {data && (
              <PieChart
                series={[
                  {
                    type: "pie",
                    name: t("apartments"),
                    data: data.byCity.map((item) => ({
                      name: item.name,
                      y: item.value,
                    })),
                  },
                ]}
              />
            )}
          </CollapsibleSection>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <CollapsibleSection
            title={t("unitsDistribution")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <LineChart
                series={[
                  {
                    type: "column",
                    name: t("apartments"),
                    data: data.unitsDistribution.map((item) => item.value),
                  },
                ]}
                xAxis={{
                  categories: data.unitsDistribution.map((item) => item.name),
                  title: { text: t("unitCount") },
                }}
                yAxis={{ title: { text: t("count") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("topApartmentsByConsumption")}
            className="xl:col-span-2"
            collapsible={false}
            isLoading={isLoading}
            skeleton="table"
            minHeight={GRID_HEIGHT}
          >
            <Grid
              gridKey="apartmentsReportTop"
              id="apartmentsReportTopGrid"
              gridName={t("topApartmentsByConsumption")}
              dataSource={data?.topApartmentsByConsumption || []}
              keyExpr="id"
              columns={topApartmentsColumns}
              rowContextMenu={apartmentDetailsContextMenu}
              height={GRID_HEIGHT}
              hasSearchPanel={false}
              hasPagination={false}
              hasGroupingFeatures={false}
              hasFilterBuilder={false}
              allowedChangeScrolling={false}
            />
          </CollapsibleSection>
        </div>
      </div>
    </>
  );
};

export default ApartmentsReportView;
